import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useFinancialData } from '../hooks/useFinancialData';
import { FinancialOverviewCard } from '../components/ui/FinancialOverviewCard';
import { TrendChart } from '../components/charts/AdvancedCharts';
import { transactionsService } from '../services/transactions';
import { goalsService, Goal } from '../services/goals';
import { formatCurrency } from '../utils/formatters';
import { Transaction } from '../types';
import { TrendingUp, TrendingDown, ArrowUpRight, CreditCard, Activity, Wallet } from 'lucide-react';

const StyledDashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media ${({ theme }) => theme.media.maxSm} {
    padding: 1rem;
  }
`;

const StyledHeader = styled.div`
  margin-bottom: 2rem;
`;

const StyledTitle = styled.h1`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;

  @media ${({ theme }) => theme.media.maxMd} {
    font-size: 2rem;
  }

  @media ${({ theme }) => theme.media.maxSm} {
    font-size: 1.75rem;
  }
`;

const StyledSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
  margin: 0;
`;

const StyledQuickStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media ${({ theme }) => theme.media.maxSm} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const StyledQuickStatCard = styled(motion.div)<{ $variant: 'primary' | 'success' | 'warning' | 'info' }>`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme, $variant }) => {
      switch ($variant) {
        case 'success':
          return theme.colors.success;
        case 'warning':
          return theme.colors.warning;
        case 'info':
          return theme.colors.accent;
        default:
          return theme.colors.primary;
      }
    }};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme, $variant }) => {
      switch ($variant) {
        case 'success':
          return theme.colors.success;
        case 'warning':
          return theme.colors.warning;
        case 'info':
          return theme.colors.accent;
        default:
          return theme.colors.primary;
      }
    }};
  }

  @media ${({ theme }) => theme.media.maxSm} {
    padding: 1rem;
  }
`;

const StyledQuickStatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const StyledQuickStatTitle = styled.h3`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StyledQuickStatIcon = styled.div<{ $variant: 'primary' | 'success' | 'warning' | 'info' }>`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
        return `${theme.colors.success}15`;
      case 'warning':
        return `${theme.colors.warning}15`;
      case 'info':
        return `${theme.colors.accent}15`;
      default:
        return `${theme.colors.primary}15`;
    }
  }};
  color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
        return theme.colors.success;
      case 'warning':
        return theme.colors.warning;
      case 'info':
        return theme.colors.accent;
      default:
        return theme.colors.primary;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledQuickStatValue = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;

  @media ${({ theme }) => theme.media.maxSm} {
    font-size: 1.5rem;
  }
`;

const StyledQuickStatChange = styled.div<{ $positive: boolean }>`
  color: ${({ theme, $positive }) => ($positive ? theme.colors.success : theme.colors.danger)};
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StyledMainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media ${({ theme }) => theme.media.maxLg} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const StyledCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const StyledCardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledCardTitle = styled.h2`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

const StyledCardContent = styled.div`
  padding: 1.5rem;
`;

const StyledTransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const StyledTransactionInfo = styled.div`
  flex: 1;
`;

const StyledTransactionTitle = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const StyledTransactionMeta = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
`;

const StyledTransactionAmount = styled.div<{ $type: 'income' | 'expense' }>`
  color: ${({ theme, $type }) => ($type === 'income' ? theme.colors.success : theme.colors.danger)};
  font-weight: 600;
  font-size: 1rem;
`;

const StyledGoalItem = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledGoalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const StyledGoalTitle = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
`;

const StyledGoalProgress = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
`;

const StyledProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
`;

const StyledProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: ${({ theme }) => theme.colors.success};
  width: ${({ $progress }) => Math.min($progress, 100)}%;
  transition: width 0.3s ease;
`;

const StyledEmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StyledLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StyledErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.danger};
`;

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { overview, isLoading, error } = useFinancialData();
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [activeGoals, setActiveGoals] = useState<Goal[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setDashboardLoading(true);
        const [transactions, goals] = await Promise.all([
          transactionsService.getTransactions(),
          goalsService.getGoals(),
        ]);

        // Get recent transactions (last 5)
        setRecentTransactions(transactions.slice(0, 5));

        // Get active goals (first 3)
        setActiveGoals(goals.filter((goal) => goal.status === 'active').slice(0, 3));
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setDashboardLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const getCurrentDate = () =>
    new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const getExpenseChange = () =>
    // Mock calculation - in real app, compare with previous period
    Math.random() > 0.5 ? 5.2 : -3.1;
  const getIncomeChange = () =>
    // Mock calculation - in real app, compare with previous period
    Math.random() > 0.5 ? 8.1 : -2.4;

  if (isLoading || dashboardLoading) {
    return (
      <StyledDashboardContainer>
        <StyledLoadingContainer>{t('common.loading')}</StyledLoadingContainer>
      </StyledDashboardContainer>
    );
  }

  if (error) {
    return (
      <StyledDashboardContainer>
        <StyledErrorContainer>{error}</StyledErrorContainer>
      </StyledDashboardContainer>
    );
  }

  if (!overview) {
    return (
      <StyledDashboardContainer>
        <StyledErrorContainer>No data available</StyledErrorContainer>
      </StyledDashboardContainer>
    );
  }

  const expenseChange = getExpenseChange();
  const incomeChange = getIncomeChange();

  return (
    <StyledDashboardContainer>
      <StyledHeader>
        <StyledTitle>{t('dashboard.title')}</StyledTitle>
        <StyledSubtitle>{getCurrentDate()}</StyledSubtitle>
      </StyledHeader>

      <StyledQuickStatsGrid>
        <StyledQuickStatCard
          $variant='primary'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StyledQuickStatHeader>
            <StyledQuickStatTitle>{t('dashboard.totalBalance')}</StyledQuickStatTitle>
            <StyledQuickStatIcon $variant='primary'>
              <Wallet size={20} />
            </StyledQuickStatIcon>
          </StyledQuickStatHeader>
          <StyledQuickStatValue>{formatCurrency(overview.totalBalance)}</StyledQuickStatValue>
          <StyledQuickStatChange $positive={true}>
            <TrendingUp size={14} />
            +2.4% from last month
          </StyledQuickStatChange>
        </StyledQuickStatCard>

        <StyledQuickStatCard
          $variant='success'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StyledQuickStatHeader>
            <StyledQuickStatTitle>{t('dashboard.monthlyIncome')}</StyledQuickStatTitle>
            <StyledQuickStatIcon $variant='success'>
              <ArrowUpRight size={20} />
            </StyledQuickStatIcon>
          </StyledQuickStatHeader>
          <StyledQuickStatValue>{formatCurrency(overview.monthlyIncome)}</StyledQuickStatValue>
          <StyledQuickStatChange $positive={incomeChange > 0}>
            {incomeChange > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {incomeChange > 0 ? '+' : ''}
            {incomeChange.toFixed(1)}% from last month
          </StyledQuickStatChange>
        </StyledQuickStatCard>

        <StyledQuickStatCard
          $variant='warning'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StyledQuickStatHeader>
            <StyledQuickStatTitle>{t('dashboard.monthlyExpenses')}</StyledQuickStatTitle>
            <StyledQuickStatIcon $variant='warning'>
              <CreditCard size={20} />
            </StyledQuickStatIcon>
          </StyledQuickStatHeader>
          <StyledQuickStatValue>{formatCurrency(overview.monthlyExpenses)}</StyledQuickStatValue>
          <StyledQuickStatChange $positive={expenseChange < 0}>
            {expenseChange > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {expenseChange > 0 ? '+' : ''}
            {expenseChange.toFixed(1)}% from last month
          </StyledQuickStatChange>
        </StyledQuickStatCard>

        <StyledQuickStatCard
          $variant='info'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <StyledQuickStatHeader>
            <StyledQuickStatTitle>{t('dashboard.netIncome')}</StyledQuickStatTitle>
            <StyledQuickStatIcon $variant='info'>
              <Activity size={20} />
            </StyledQuickStatIcon>
          </StyledQuickStatHeader>
          <StyledQuickStatValue>{formatCurrency(overview.netIncome)}</StyledQuickStatValue>
          <StyledQuickStatChange $positive={overview.netIncome > 0}>
            {overview.netIncome > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {overview.netIncome > 0 ? 'Positive' : 'Negative'} cash flow
          </StyledQuickStatChange>
        </StyledQuickStatCard>
      </StyledQuickStatsGrid>

      <FinancialOverviewCard overview={overview} loading={isLoading} />

      <TrendChart
        data={[
          { month: 'Jan', income: 5000, expenses: 3500 },
          { month: 'Feb', income: 5200, expenses: 3200 },
          { month: 'Mar', income: 4800, expenses: 3800 },
          { month: 'Apr', income: 5500, expenses: 4000 },
          { month: 'May', income: 5300, expenses: 3600 },
          { month: 'Jun', income: 5600, expenses: 3900 },
        ]}
        title={t('dashboard.monthlyTrends') || 'Monthly Trends'}
        height={300}
      />

      <StyledMainGrid>
        <StyledCard>
          <StyledCardHeader>
            <StyledCardTitle>{t('dashboard.recentTransactions')}</StyledCardTitle>
          </StyledCardHeader>
          <StyledCardContent>
            {recentTransactions.length === 0 ? (
              <StyledEmptyState>{t('transactions.empty.title')}</StyledEmptyState>
            ) : (
              recentTransactions.map((transaction) => (
                <StyledTransactionItem key={transaction.id}>
                  <StyledTransactionInfo>
                    <StyledTransactionTitle>{transaction.description}</StyledTransactionTitle>
                    <StyledTransactionMeta>
                      {transaction.category} • {new Date(transaction.date).toLocaleDateString()}
                    </StyledTransactionMeta>
                  </StyledTransactionInfo>
                  <StyledTransactionAmount $type={transaction.type}>
                    {transaction.type === 'income' ? '+' : ''}
                    {formatCurrency(transaction.amount)}
                  </StyledTransactionAmount>
                </StyledTransactionItem>
              ))
            )}
          </StyledCardContent>
        </StyledCard>

        <StyledCard>
          <StyledCardHeader>
            <StyledCardTitle>{t('dashboard.activeGoals')}</StyledCardTitle>
          </StyledCardHeader>
          <StyledCardContent>
            {activeGoals.length === 0 ? (
              <StyledEmptyState>{t('goals.empty.title')}</StyledEmptyState>
            ) : (
              activeGoals.map((goal) => {
                const progress = (goal.currentAmount / goal.targetAmount) * 100;

                return (
                  <StyledGoalItem key={goal.id}>
                    <StyledGoalHeader>
                      <StyledGoalTitle>{goal.title}</StyledGoalTitle>
                      <StyledGoalProgress>{Math.round(progress)}%</StyledGoalProgress>
                    </StyledGoalHeader>
                    <StyledProgressBar>
                      <StyledProgressFill $progress={progress} />
                    </StyledProgressBar>
                  </StyledGoalItem>
                );
              })
            )}
          </StyledCardContent>
        </StyledCard>
      </StyledMainGrid>
    </StyledDashboardContainer>
  );
};

export default DashboardPage;
